// src/components/League/liveDraft/LiveDraftPicker.jsx
//
// Competitive Live Draft (Phase 4) — the slot picker. The week's slots
// (config-driven, from GET /api/tournament/slot-schedule) with day/time in ET and
// a per-slot human count ("Sun 7:00pm ET · 3 humans waiting"); claim/release a
// seat via the Phase-1 endpoints. Lean and functional (not the hub); behind
// LEAGUE_LIVE_DRAFT (the endpoints 404 dark, and the caller gates the mount on the
// same flag). `tokens` is the League useTheme tokens (passed in) so this stays
// pure/smoke-testable. `services` (optional) replaces the three slot calls —
// only the Backing dev preview page passes it (fixture answers, no network);
// omitted, each call is the real service, exactly as before.

import React, { useState, useEffect, useCallback } from 'react';
import { fetchSlotSchedule, claimSlot, releaseSlot, mapSlotActionError } from '../../../services/liveDraftActions';

function primaryBtn(t, enabled) {
  return { background: enabled ? t.teal : t.borderDivider, color: enabled ? '#04121f' : t.textFaint, border: 'none', borderRadius: 9, padding: '9px 16px', fontWeight: 700, cursor: enabled ? 'pointer' : 'default', whiteSpace: 'nowrap' };
}
function ghostBtn(t, enabled) {
  return { background: 'transparent', color: t.textMuted, border: `1px solid ${t.borderDivider}`, borderRadius: 9, padding: '9px 16px', fontWeight: 600, cursor: enabled ? 'pointer' : 'default', whiteSpace: 'nowrap' };
}

export default function LiveDraftPicker({ tokens, currentUserId, displayName = null, onEntered = null, services = null }) {
  const fetchSchedule = services?.fetchSlotSchedule ?? fetchSlotSchedule;
  const claim = services?.claimSlot ?? claimSlot;
  const release = services?.releaseSlot ?? releaseSlot;
  const [slots, setSlots] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setError(null); setSlots(null);
    try { const r = await fetchSchedule(); setSlots(Array.isArray(r?.slots) ? r.slots : []); }
    catch (e) { setError(mapSlotActionError(e)); setSlots([]); }
  }, [fetchSchedule]);
  useEffect(() => { load(); }, [load]);

  // `entered` fires onEntered for the CLAIM payoff only — a release must never
  // route the user into the game surface they just left.
  const run = async (fn, { entered = false } = {}) => {
    setPending(true); setError(null);
    try {
      await fn();
      if (entered && onEntered) onEntered();
      else await load();
    }
    catch (e) { setError(mapSlotActionError(e)); }
    finally { setPending(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>Pick a draft slot</div>
      <p style={{ color: tokens.textMuted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
        Choose a draft time. If you miss it, your picks are drafted automatically. Empty seats fill with CPUs.
      </p>

      {error && (
        <div role="alert" style={{ color: tokens.textMuted, background: tokens.bgCard, border: `1px solid ${tokens.borderDivider}`, borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
          {error}
          <button onClick={load} disabled={pending} style={{ ...ghostBtn(tokens, !pending), marginLeft: 8 }}>Retry slots</button>
        </div>
      )}

      {slots == null ? (
        <div style={{ color: tokens.textMuted, padding: 16, textAlign: 'center', fontSize: 13 }}>Loading slots…</div>
      ) : slots.length === 0 && !error ? (
        <div style={{ color: tokens.textMuted, padding: 16, textAlign: 'center', fontSize: 13 }}>No draft slots scheduled right now.</div>
      ) : (
        slots.map((slot) => {
          const mine = (slot.seats || []).some((s) => s.odUserId === currentUserId);
          // Default-enabled, matching the server (liveDraftSlots.js): only an
          // explicit `false` closes the door, so a payload from before the field
          // existed still renders a claimable row. A seated user keeps their
          // Leave button on a disabled slot — never strand someone in a slot
          // that was taken off the board under them.
          const disabled = slot.enabled === false;
          const claimable = !pending && !slot.isFull && !disabled;
          return (
            <div key={slot.slotId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: tokens.bgCard, border: `1px solid ${mine ? tokens.medalGold : tokens.borderDivider}`, borderRadius: 12, padding: '12px 14px', opacity: disabled && !mine ? 0.55 : 1 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{slot.label}</div>
                <div style={{ fontSize: 12, color: tokens.textMuted }}>
                  {disabled
                    ? 'Unavailable right now'
                    : slot.humanCount === 0 ? 'No one yet — be the first' : `${slot.humanCount} human${slot.humanCount === 1 ? '' : 's'} waiting`}
                  {!disabled && slot.isFull ? ' · full' : ''}
                </div>
              </div>
              {mine ? (
                <button onClick={() => run(() => release({ groupId: slot.groupId }))} disabled={pending} style={ghostBtn(tokens, !pending)}>Leave</button>
              ) : (
                <button onClick={() => run(() => claim({ slotId: slot.slotId, displayName }), { entered: true })} disabled={!claimable} style={primaryBtn(tokens, claimable)}>
                  {disabled ? 'Unavailable' : slot.isFull ? 'Full' : 'Claim seat'}
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
